import { useCallback, useState } from 'react';
import { feedback } from 'react-feedbacker';

import { useIsMounted } from './useIsMounted';

type BatchError = {
  batchIndex: number;
  error: Error;
};

type BatchProcessingResult<R> = {
  results: (R | null)[];
  errors: BatchError[];
  successfulBatches: number;
  failedBatches: number;
};

type BatchSuccessResult<R> = {
  results: (R | null)[];
  successfulBatches: number;
  totalBatches: number;
};

type BatchCallbacks<R> = {
  onSuccess?: (result: BatchSuccessResult<R>) => void;
  onPartialSuccess?: (result: BatchProcessingResult<R>) => void;
  onError?: (errors: BatchError[]) => void;
};

type ProcessorOptions<T, R> = {
  batchSize?: number;
  maxRetries?: number;
  retryDelay?: number;
  batchDelay?: number;
  fallbackThreshold?: number;
  processor: (batch: T[], batchIndex: number) => Promise<R>;
  fallbackProcessor?: (items: T[]) => Promise<R>;
} & BatchCallbacks<R>;

export type BatchProgress = {
  isProcessing: boolean;
  currentBatch: number;
  totalBatches: number;
  processedItems: number;
  totalItems: number;
  errors: string[];
  successfulBatches: number;
  percentage: number;
};

export type BatchProcessorHookOptions = {
  enableFeedback?: boolean;
  onClose?: () => void;
};

const DEFAULT_BATCH_SIZE = 1000; // Default batch size for processing
const DEFAULT_MAX_RETRIES = 2; // Default maximum retries for failed batches
const DEFAULT_RETRY_DELAY = 1000; // Default delay between retries (in ms)
const DEFAULT_BATCH_DELAY = 1000; // Default delay between batches (in ms) to bypass the limit set by the API

// Simple delay helper function
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Chunk an array into smaller arrays of a specified size (functional approach)
const chunkArray = <T>(array: T[], size: number): T[][] => {
  return Array.from({ length: Math.ceil(array.length / size) }, (_, index) =>
    array.slice(index * size, (index + 1) * size),
  );
};

// Helper function to handle errors and delays
const handleProcessingError = async (params: {
  error: unknown;
  batchIndex: number;
  retryCount: number;
  maxRetries: number;
  retryDelay: number;
  onError?: (error: Error, batchIndex: number, retryCount: number) => void;
}): Promise<Error> => {
  const { error, batchIndex, retryCount, maxRetries, retryDelay, onError } =
    params;
  const processedError =
    error instanceof Error ? error : new Error(String(error));

  onError?.(processedError, batchIndex, retryCount + 1);

  // Don't delay after the last retry attempt
  if (retryCount < maxRetries) {
    await delay(retryDelay);
  }

  return processedError;
};

// Process a single batch with retry logic
const batchProcessor = async <T, R>(params: {
  batch: T[];
  batchIndex: number;
  processor: (batch: T[], batchIndex: number) => Promise<R>;
  options: {
    maxRetries: number;
    retryDelay: number;
    onError?: (error: Error, batchIndex: number, retryCount: number) => void;
  };
}): Promise<{ result: R | null; error: Error | null }> => {
  const { batch, batchIndex, processor, options } = params;
  const { maxRetries, retryDelay, onError } = options;
  let lastError: Error | null = null;

  for (let retryCount = 0; retryCount <= maxRetries; retryCount++) {
    try {
      const result = await processor(batch, batchIndex);
      return { result, error: null };
    } catch (error) {
      lastError = await handleProcessingError({
        error,
        batchIndex,
        retryCount,
        maxRetries,
        retryDelay,
        onError,
      });
    }
  }

  return { result: null, error: lastError };
};

// Helper function to create feedback-enhanced callbacks
const createEnhancedCallbacks = <R>(
  enableFeedback: boolean,
  onClose: (() => void) | undefined,
  originalCallbacks: BatchCallbacks<R>,
) => {
  if (!enableFeedback) {
    return originalCallbacks;
  }

  return {
    onSuccess: (result: BatchSuccessResult<R>) => {
      const message = `All batches processed successfully!`;
      feedback.success(message);
      onClose?.();
      originalCallbacks.onSuccess?.(result);
    },
    onPartialSuccess: (result: BatchProcessingResult<R>) => {
      const message = `Partial success: ${result.successfulBatches}/${result.successfulBatches + result.failedBatches} batches processed successfully.`;
      feedback.warning(message);
      originalCallbacks.onPartialSuccess?.(result);
    },
    onError: (errors: BatchError[]) => {
      const message = 'All batches failed.';
      feedback.error(message);
      originalCallbacks.onError?.(errors);
    },
  };
};

// Helper function to handle processing results
const handleProcessingResult = <R>(
  result: BatchProcessingResult<R>,
  enhancedCallbacks: BatchCallbacks<R>,
) => {
  if (result.errors.length === 0) {
    enhancedCallbacks.onSuccess?.({
      results: result.results,
      successfulBatches: result.successfulBatches,
      totalBatches: result.successfulBatches + result.failedBatches,
    });
  } else if (result.successfulBatches > 0) {
    enhancedCallbacks.onPartialSuccess?.({
      results: result.results,
      successfulBatches: result.successfulBatches,
      failedBatches: result.failedBatches,
      errors: result.errors,
    });
  } else {
    enhancedCallbacks.onError?.(result.errors);
  }
};

export const useBatchProcessor = <T, R>(
  options: BatchProcessorHookOptions = {},
) => {
  const { enableFeedback = false, onClose } = options;

  const [progress, setProgress] = useState<BatchProgress>({
    isProcessing: false,
    currentBatch: 0,
    totalBatches: 0,
    processedItems: 0,
    totalItems: 0,
    errors: [],
    successfulBatches: 0,
    percentage: 0,
  });

  const isMounted = useIsMounted();

  const processBatches = useCallback(
    async (items: T[], processingOptions: ProcessorOptions<T, R>) => {
      const {
        batchSize = DEFAULT_BATCH_SIZE,
        maxRetries = DEFAULT_MAX_RETRIES,
        retryDelay = DEFAULT_RETRY_DELAY,
        batchDelay = DEFAULT_BATCH_DELAY,
        fallbackThreshold,
        onSuccess,
        onPartialSuccess,
        onError,
        processor,
        fallbackProcessor,
      } = processingOptions;

      const enhancedCallbacks = createEnhancedCallbacks(
        enableFeedback,
        onClose,
        { onSuccess, onPartialSuccess, onError },
      );

      const threshold = fallbackThreshold ?? batchSize;
      const shouldUseBatching = items.length > threshold;

      setProgress((prev) => ({ ...prev, isProcessing: true }));

      try {
        let result: BatchProcessingResult<R>;

        if (!shouldUseBatching && fallbackProcessor) {
          const fallbackResult = await fallbackProcessor(items);

          result = {
            results: [fallbackResult],
            errors: [],
            successfulBatches: 1,
            failedBatches: 0,
          };
        } else {
          // Process batches sequentially with progress tracking
          const numericBatchSize = Number(batchSize);
          const batches = chunkArray(items, numericBatchSize);

          result = await batches.reduce(
            async (accPromise, batch, batchIndex) => {
              const acc = await accPromise;

              const { result: batchResult, error } = await batchProcessor({
                batch,
                batchIndex,
                processor,
                options: {
                  maxRetries,
                  retryDelay,
                  onError: (error, batchIndex, retryCount) => {
                    if (isMounted) {
                      setProgress((prev) => ({
                        ...prev,
                        errors: [
                          ...prev.errors,
                          `Batch ${batchIndex + 1} failed after ${retryCount} retries: ${error.message}`,
                        ],
                      }));
                    }
                  },
                },
              });

              acc.results.push(batchResult);

              if (batchResult !== null) {
                acc.successfulBatches++;
              } else {
                acc.failedBatches++;
              }

              if (error) {
                acc.errors.push({ batchIndex, error });
              }

              // Update progress
              const progressData = {
                currentBatch: batchIndex + 1,
                totalBatches: batches.length,
                processedItems: Math.min(
                  (batchIndex + 1) * numericBatchSize,
                  items.length,
                ),
                totalItems: items.length,
                percentage: ((batchIndex + 1) / batches.length) * 100,
                successfulBatches: acc.successfulBatches,
                failedBatches: acc.failedBatches,
              };

              if (isMounted) {
                setProgress((prev) => ({
                  ...prev,
                  ...progressData,
                  errors: [],
                }));
              }

              // Delay between batches
              await delay(batchDelay);

              return acc;
            },
            Promise.resolve({
              results: [] as (R | null)[],
              errors: [] as BatchError[],
              successfulBatches: 0,
              failedBatches: 0,
            } as BatchProcessingResult<R>),
          );
        }

        // Convert errors to string messages for UI
        const errorMessages = result.errors.map(
          ({ batchIndex, error }) =>
            `Batch ${batchIndex + 1}: ${error.message}`,
        );

        if (isMounted) {
          setProgress((prev) => ({
            ...prev,
            isProcessing: false,
            errors: errorMessages,
          }));
        }

        if (!isMounted) {
          return;
        }

        handleProcessingResult(result, enhancedCallbacks);
        return result;
      } catch (error) {
        if (isMounted) {
          setProgress((prev) => ({ ...prev, isProcessing: false }));

          const errorObj =
            error instanceof Error ? error : new Error(String(error));
          enhancedCallbacks.onError?.([{ batchIndex: -1, error: errorObj }]);
        }

        throw error;
      }
    },
    [isMounted, enableFeedback, onClose],
  );

  return {
    progress,
    processBatches,
    isProcessing: progress.isProcessing,
  };
};
